/**
 * SpotifyCallbackPage
 *
 * Route: /spotify-callback
 *
 * Spotify redirects back here after the user authorizes (or denies) access.
 * Expected query parameters:
 *   Success: ?code=<authorization_code>&state=<state>
 *   Denied:  ?error=<reason>&state=<state>
 *
 * On success: delegates token exchange to SpotifyAuthService.handleCallback(),
 *             then navigates to /spotify-profile.
 * On failure: displays the reason and offers a "Try Again" button.
 */

import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { SpotifyAuthService } from '../../services/spotify-auth.service';

@Component({
  selector: 'app-spotify-callback',
  standalone: true,
  imports: [IonicModule, CommonModule, RouterModule],
  templateUrl: './spotify-callback.page.html',
  styleUrls: ['./spotify-callback.page.scss'],
})
export class SpotifyCallbackPage implements OnInit {
  status: 'loading' | 'success' | 'error' = 'loading';
  errorMessage = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private spotifyAuth: SpotifyAuthService,
  ) {}

  async ngOnInit(): Promise<void> {
    const params = this.route.snapshot.queryParamMap;
    const code   = params.get('code');
    const state  = params.get('state');
    const error  = params.get('error');

    // User denied access or Spotify returned an error
    if (error) {
      this.status       = 'error';
      this.errorMessage = `Spotify denied access: ${error}`;
      return;
    }

    if (!code || !state) {
      this.status       = 'error';
      this.errorMessage = 'Missing authorization code or state parameter.';
      return;
    }

    try {
      await this.spotifyAuth.handleCallback(code, state);
      this.status = 'success';
      // Short pause so the success state is visible before navigation
      setTimeout(() => this.router.navigate(['/spotify-profile']), 800);
    } catch (err: any) {
      this.status       = 'error';
      this.errorMessage = err?.message ?? 'Token exchange failed. Please try again.';
    }
  }

  /** Re-initiates the full login flow from the error state. */
  retryLogin(): void {
    this.spotifyAuth.login();
  }
}
